import * as STD                    from '@emersya/mpa-std-lib';
import CM                          from '@emersya/mpa-components-manager-lib';
import { NS as StandingStorageNS } from '../StandingStorage/StandingStorage';
import ViewerComponent             from '../../../viewer/Viewer/Viewer';

export interface IStandingLightningComponentConfiguration extends CM.Component.IConfiguration {
	// To not duplicate many property of the app configuration, and because this component is unique, retrieve conf directly from app and lets this interface be empty.
};

export class StandingLightningComponent extends CM.Component<
	StandingLightningComponent,
	IStandingLightningComponentConfiguration
>
{
	readonly plannerCategory! : ViewerComponent.ProductCategory;
	viewerNode : ViewerComponent.ProductInstance | null = null;
	lightsNodesLocalId : number[] = [];

	getWDH() {
		const p = app.currentProduct;
		if (!p) {
			this.component_throwUserError('');
		}
		const dpart = p.getPart([ 'dimensions' ]);
		return {
			width :  Number.parseFloat(dpart.getPart([ 'width' ])?.reference?.value || <string> <any> 1),
			depth :  Number.parseFloat(dpart.getPart([ 'depth' ])?.reference?.value || <string> <any> 1),
			height : Number.parseFloat(dpart.getPart([ 'height' ])?.reference?.value || <string> <any> 1)
		};
	};

	getWallThickness() {
		// TODO : DEHARDCODE
		return 0.05;
	}

	async insert() {
		// insert nodes
		const product = app.currentProduct;
		if (this.viewerNode || !product || !app.viewer.planner) {
			this.component_throwUserError('');
		}
		const lightingProductSchema = this.plannerCategory.products.find(x => x.SKU === '@lighting');
		if (!lightingProductSchema) {
			this.component_throwLibraryError('');
		}
		this.viewerNode = await lightingProductSchema.instantiate({
			rootLocalId : app.viewer.globalLocalId,
			appendType : 'insert',
			basketPart : null,
			basketUseDynamicTitleBinding : false,
			metadata : {
				id : this.id
			}
		});
		this.viewerNode.setName('lighting');
	};

	async update() {
		// update nodes matrices
		if (!this.viewerNode) {
			return;
		}
		const product = app.currentProduct;
		if (!product) {
			throw STD.errors.user('No current product available!');
		}
		const layoutPart = product.getPart([ 'layout' ]);
		if (!layoutPart) {
			throw STD.errors.user('No current product available!');
		}
		const layoutCurrentOption = layoutPart.currentOption;
		if (!layoutCurrentOption) {
			return;
		}
		const wdh = this.getWDH();
		const x = wdh.width;
		const z = wdh.depth;
		const updates = <any[]> [];
		if (this.lightsNodesLocalId.length > 0) {
			await app.viewer.removeProductNodesInstances(this.lightsNodesLocalId);
			this.lightsNodesLocalId = [];
		}
		const lightingProject3Spots = app.component_configuration.productsComponents.find(x => x.type ===
			'lighting' &&
			x.SKU ===
			'lighting3');
		const lightingProject2Spots = app.component_configuration.productsComponents.find(x => x.type ===
			'lighting' &&
			x.SKU ===
			'lighting2');
		if (!lightingProject3Spots || !lightingProject2Spots) {
			throw STD.errors.user('No lighting found');
		}
		const boundingBoxlighting3 = lightingProject3Spots.boundingBox;
		const boundingBoxlighting2 = lightingProject2Spots.boundingBox;
		if (!boundingBoxlighting3 || !boundingBoxlighting2) {
			throw STD.errors.user('Missing bounding box for lighting');
		}
		if (!app.viewer.planner) {
			throw STD.errors.user('Missing planner');
		}
		if (!this.viewerNode.idMapping.lightingWallLeft ||
			!this.viewerNode.idMapping.lightingWallBack ||
			!this.viewerNode.idMapping.lightingWallRight)
		{
			throw STD.errors.user('missing nodes in lighting');
		}
		await app.viewer.batch([
			[
				'setProductNodesInstancesMatrices', [
				{
					localId : this.viewerNode.idMapping.lightingWallLeft,
					matrix :  app.viewer.mtx([ -x / 2, 0, z / 2 ], [ 1, 1, 1 ], 90)
				},
				{
					localId : this.viewerNode.idMapping.lightingWallBack,
					matrix :  app.viewer.mtx([ -x / 2, 0, -z / 2 ], [ 1, 1, 1 ], 0)
				},
				{
					localId : this.viewerNode.idMapping.lightingWallRight,
					matrix :  app.viewer.mtx([ x / 2, 0, -z / 2 ], [ 1, 1, 1 ], 270)
				}
			]
			]
		]);
		const storage = app.modules.used.find(x => x.id === 'standing-storage');
		if (!storage) {
			throw STD.errors.user('Storage not instanciate before lighting');
		}
		const storageComponent = <StandingStorageNS.Component> storage.component;
		const positionStorage = storageComponent.positionStorage;
		const startStorage : number = positionStorage.start;
		const endStorage : number = positionStorage.end;
		const surface = x * z;
		const nbreOfSpots = Math.round(surface / 3);
		let nbreOf3Spots = 0;
		let nbreOf2spots = 0;
		if (nbreOfSpots % 3 === 0) {
			nbreOf3Spots = nbreOfSpots / 3;
		} else if (nbreOfSpots % 3 === 1) {
			nbreOf3Spots = Math.round(nbreOfSpots / 3) - 1;
			nbreOf2spots = 2;
		} else if (nbreOfSpots % 3 === 2) {
			nbreOf3Spots = Math.round(nbreOfSpots / 3) - 1;
			nbreOf2spots = 1;
		}
		let currentNbreOf3Spots = 0;
		let currentNbreOf2Spots = 0;
		if (layoutCurrentOption.id === 'shape-no-wall') {
			return;
		} else if (layoutCurrentOption.id === 'shape-back-left') {
			const perim = x + z;
			let incr = 0;
			let start = 0;
			if (Math.round(perim) % 3 === 0) {
				incr = 3;
				start = 1.5;
			} else if (Math.round(perim) % 3 === 1) {
				incr = 10 / 3;
				start = 5 / 3;
			} else {
				incr = 8 / 3;
				start = 4 / 3;
			}
			let ratio = (nbreOf3Spots + nbreOf2spots) * incr / (perim - start);
			if (ratio > 1.5) {
				ratio = 1.5;
			}
			incr = incr / ratio;
			for (let i = start; i < perim; i += incr) {
				if ((i > startStorage - 0.5) && (i < endStorage + 0.5)) {
					if ((surface > 24 && product.id === 'siae') ||
						product.id === 'confort' ||
						product.id === 'business' ||
						product.id === 'prestige')
					{
						currentNbreOf3Spots += 1;
						if (i + incr < endStorage + 0.5) {
							i = endStorage + 1 - incr;
						}
					} else {
						i = endStorage + 0.7;
						const parentLocalId = this.viewerNode.idMapping.lightingWallBack;
						if (currentNbreOf3Spots < nbreOf3Spots) {
							currentNbreOf3Spots += 1;
							const node = {
								projectCode :   lightingProject3Spots.viewerProjectCode,
								SKU :           lightingProject3Spots.viewerComponentSKU,
								localId :       app.viewer.getLocalId(),
								parentLocalId : parentLocalId,
								matrix :        app.viewer.mtx(
									[ i - z, 0, 0 ],
									[ 1, 1, 1 ],
									0
								),
							};
							this.lightsNodesLocalId.push(node.localId);
							updates.push(node);
						} else if (currentNbreOf2Spots < nbreOf2spots) {
							currentNbreOf2Spots += 1;
							const node = {
								projectCode :   lightingProject2Spots.viewerProjectCode,
								SKU :           lightingProject2Spots.viewerComponentSKU,
								localId :       app.viewer.getLocalId(),
								parentLocalId : parentLocalId,
								matrix :        app.viewer.mtx(
									[ i - z, 0, 0 ],
									[ 1, 1, 1 ],
									0
								),
							};
							this.lightsNodesLocalId.push(node.localId);
							updates.push(node);
						}
					}
				} else if (i < z - 0.5) {
					if (currentNbreOf3Spots < nbreOf3Spots) {
						currentNbreOf3Spots += 1;
						const parentLocalId = this.viewerNode.idMapping.lightingWallLeft;
						const node = {
							projectCode :   lightingProject3Spots.viewerProjectCode,
							SKU :           lightingProject3Spots.viewerComponentSKU,
							localId :       app.viewer.getLocalId(),
							parentLocalId : parentLocalId,
							matrix :        app.viewer.mtx(
								[ i, 0, 0 ],
								[ 1, 1, 1 ],
								0
							),
						};
						this.lightsNodesLocalId.push(node.localId);
						updates.push(node);
					} else if (currentNbreOf2Spots < nbreOf2spots) {
						currentNbreOf2Spots += 1;
						const parentLocalId = this.viewerNode.idMapping.lightingWallLeft;
						const node = {
							projectCode :   lightingProject2Spots.viewerProjectCode,
							SKU :           lightingProject2Spots.viewerComponentSKU,
							localId :       app.viewer.getLocalId(),
							parentLocalId : parentLocalId,
							matrix :        app.viewer.mtx(
								[ i, 0, 0 ],
								[ 1, 1, 1 ],
								0
							),
						};
						this.lightsNodesLocalId.push(node.localId);
						updates.push(node);
					}
				} else {
					const parentLocalId = this.viewerNode.idMapping.lightingWallBack;
					if (i < z + 0.6) {
						if (currentNbreOf3Spots < nbreOf3Spots) {
							currentNbreOf3Spots += 1;
							const node = {
								projectCode :   lightingProject3Spots.viewerProjectCode,
								SKU :           lightingProject3Spots.viewerComponentSKU,
								localId :       app.viewer.getLocalId(),
								parentLocalId : parentLocalId,
								matrix :        app.viewer.mtx(
									[ 0.6, 0, 0 ],
									[ 1, 1, 1 ],
									0
								),
							};
							this.lightsNodesLocalId.push(node.localId);
							updates.push(node);
						} else if (currentNbreOf2Spots < nbreOf2spots) {
							currentNbreOf2Spots += 1;
							const node = {
								projectCode :   lightingProject2Spots.viewerProjectCode,
								SKU :           lightingProject2Spots.viewerComponentSKU,
								localId :       app.viewer.getLocalId(),
								parentLocalId : parentLocalId,
								matrix :        app.viewer.mtx(
									[ 0.6, 0, 0 ],
									[ 1, 1, 1 ],
									0
								),
							};
							this.lightsNodesLocalId.push(node.localId);
							updates.push(node);
						}
						i = z + 0.6;
					} else {
						if (i > x + z - 1.1 && i < x + z - 0.5) {
							i = x + z - 1.2;
							if (currentNbreOf3Spots < nbreOf3Spots) {
								currentNbreOf3Spots += 1;
								const node = {
									projectCode :   lightingProject3Spots.viewerProjectCode,
									SKU :           lightingProject3Spots.viewerComponentSKU,
									localId :       app.viewer.getLocalId(),
									parentLocalId : parentLocalId,
									matrix :        app.viewer.mtx(
										[ i - z, 0, 0 ],
										[ 1, 1, 1 ],
										0
									),
								};
								this.lightsNodesLocalId.push(node.localId);
								updates.push(node);
							} else if (currentNbreOf2Spots < nbreOf2spots) {
								currentNbreOf2Spots += 1;
								const node = {
									projectCode :   lightingProject2Spots.viewerProjectCode,
									SKU :           lightingProject2Spots.viewerComponentSKU,
									localId :       app.viewer.getLocalId(),
									parentLocalId : parentLocalId,
									matrix :        app.viewer.mtx(
										[ i - z, 0, 0 ],
										[ 1, 1, 1 ],
										0
									),
								};
								this.lightsNodesLocalId.push(node.localId);
								updates.push(node);
							}
						} else if (i < x + z - 1.1) {
							if (currentNbreOf3Spots < nbreOf3Spots) {
								currentNbreOf3Spots += 1;
								const node = {
									projectCode :   lightingProject3Spots.viewerProjectCode,
									SKU :           lightingProject3Spots.viewerComponentSKU,
									localId :       app.viewer.getLocalId(),
									parentLocalId : parentLocalId,
									matrix :        app.viewer.mtx(
										[ i - z, 0, 0 ],
										[ 1, 1, 1 ],
										0
									),
								};
								this.lightsNodesLocalId.push(node.localId);
								updates.push(node);
							} else if (currentNbreOf2Spots < nbreOf2spots) {
								currentNbreOf2Spots += 1;
								const node = {
									projectCode :   lightingProject2Spots.viewerProjectCode,
									SKU :           lightingProject2Spots.viewerComponentSKU,
									localId :       app.viewer.getLocalId(),
									parentLocalId : parentLocalId,
									matrix :        app.viewer.mtx(
										[ i - z, 0, 0 ],
										[ 1, 1, 1 ],
										0
									),
								};
								this.lightsNodesLocalId.push(node.localId);
								updates.push(node);
							}
						}
					}
				}
			}
		} else if (layoutCurrentOption.id === 'shape-back') {
			const perim = x;
			let incr = 0;
			let start = 0;
			if (Math.round(perim) % 3 === 0) {
				incr = 3;
				start = 1.5;
			} else if (Math.round(perim) % 3 === 1) {
				incr = 10 / 3;
				start = 5 / 3;
			} else {
				incr = 8 / 3;
				start = 4 / 3;
			}
			for (let i = start; i < perim; i += incr) {
				if ((i > startStorage - 0.5) && (i < endStorage + 0.5)) {
					if ((surface > 24 && product.id === 'siae') ||
						product.id === 'confort' ||
						product.id === 'business' ||
						product.id === 'prestige')
					{
						currentNbreOf3Spots += 1;
						if (i + incr < endStorage + 0.5) {
							i = endStorage + 1 - incr;
						}
					}
				} else {
					if (i > x - 1.1 && i < x - 0.5) {
						i = x - 1.2;
						const parentLocalId = this.viewerNode.idMapping.lightingWallBack;
						if (currentNbreOf3Spots < nbreOf3Spots) {
							currentNbreOf3Spots += 1;
							const node = {
								projectCode :   lightingProject3Spots.viewerProjectCode,
								SKU :           lightingProject3Spots.viewerComponentSKU,
								localId :       app.viewer.getLocalId(),
								parentLocalId : parentLocalId,
								matrix :        app.viewer.mtx(
									[ i, 0, 0 ],
									[ 1, 1, 1 ],
									0
								),
							};
							this.lightsNodesLocalId.push(node.localId);
							updates.push(node);
						} else if (currentNbreOf2Spots < nbreOf2spots) {
							currentNbreOf2Spots += 1;
							const node = {
								projectCode :   lightingProject2Spots.viewerProjectCode,
								SKU :           lightingProject2Spots.viewerComponentSKU,
								localId :       app.viewer.getLocalId(),
								parentLocalId : parentLocalId,
								matrix :        app.viewer.mtx(
									[ i, 0, 0 ],
									[ 1, 1, 1 ],
									0
								),
							};
							this.lightsNodesLocalId.push(node.localId);
							updates.push(node);
						}
					} else if (i <= x - 1.1) {
						const parentLocalId = this.viewerNode.idMapping.lightingWallBack;
						if (currentNbreOf3Spots < nbreOf3Spots) {
							currentNbreOf3Spots += 1;
							const node = {
								projectCode :   lightingProject3Spots.viewerProjectCode,
								SKU :           lightingProject3Spots.viewerComponentSKU,
								localId :       app.viewer.getLocalId(),
								parentLocalId : parentLocalId,
								matrix :        app.viewer.mtx(
									[ i, 0, 0 ],
									[ 1, 1, 1 ],
									0
								),
							};
							this.lightsNodesLocalId.push(node.localId);
							updates.push(node);
						} else if (currentNbreOf2Spots < nbreOf2spots) {
							currentNbreOf2Spots += 1;
							const node = {
								projectCode :   lightingProject2Spots.viewerProjectCode,
								SKU :           lightingProject2Spots.viewerComponentSKU,
								localId :       app.viewer.getLocalId(),
								parentLocalId : parentLocalId,
								matrix :        app.viewer.mtx(
									[ i, 0, 0 ],
									[ 1, 1, 1 ],
									0
								),
							};
							this.lightsNodesLocalId.push(node.localId);
							updates.push(node);
						}
					}
				}
			}
		} else if (layoutCurrentOption.id === 'shape-back-right') {
			const perim = x + z;
			let incr = 0;
			let start = 0;
			if (Math.round(perim) % 3 === 0) {
				incr = 3;
				start = 1.5;
			} else if (Math.round(perim) % 3 === 1) {
				incr = 10 / 3;
				start = 5 / 3;
			} else {
				incr = 8 / 3;
				start = 4 / 3;
			}
			const ratio = (nbreOf3Spots + nbreOf2spots) * incr / (perim - start);
			incr = incr / ratio;
			for (let i = start; i < perim; i += incr) {
				if (i > startStorage - 0.5 && i < endStorage + 0.5) {
					if ((surface > 24 && product.id === 'siae') ||
						product.id === 'confort' ||
						product.id === 'business' ||
						product.id === 'prestige')
					{
						currentNbreOf3Spots += 1;
						if (i + incr < endStorage + 0.5) {
							i = endStorage + 1 - incr;
						}
					} else {
						i = endStorage + 0.7;
						const parentLocalId = this.viewerNode.idMapping.lightingWallRight;
						if (currentNbreOf3Spots < nbreOf3Spots) {
							currentNbreOf3Spots += 1;
							const node = {
								projectCode :   lightingProject3Spots.viewerProjectCode,
								SKU :           lightingProject3Spots.viewerComponentSKU,
								localId :       app.viewer.getLocalId(),
								parentLocalId : parentLocalId,
								matrix :        app.viewer.mtx(
									[ i - x, 0, 0 ],
									[ 1, 1, 1 ],
									0
								),
							};
							this.lightsNodesLocalId.push(node.localId);
							updates.push(node);
						} else if (currentNbreOf2Spots < nbreOf2spots) {
							currentNbreOf2Spots += 1;
							const node = {
								projectCode :   lightingProject2Spots.viewerProjectCode,
								SKU :           lightingProject2Spots.viewerComponentSKU,
								localId :       app.viewer.getLocalId(),
								parentLocalId : parentLocalId,
								matrix :        app.viewer.mtx(
									[ i - x, 0, 0 ],
									[ 1, 1, 1 ],
									0
								),
							};
							this.lightsNodesLocalId.push(node.localId);
							updates.push(node);
						}
					}
				} else if (i < x - 0.5) {
					const parentLocalId = this.viewerNode.idMapping.lightingWallBack;
					if (currentNbreOf3Spots < nbreOf3Spots) {
						currentNbreOf3Spots += 1;
						const node = {
							projectCode :   lightingProject3Spots.viewerProjectCode,
							SKU :           lightingProject3Spots.viewerComponentSKU,
							localId :       app.viewer.getLocalId(),
							parentLocalId : parentLocalId,
							matrix :        app.viewer.mtx(
								[ i, 0, 0 ],
								[ 1, 1, 1 ],
								0
							),
						};
						this.lightsNodesLocalId.push(node.localId);
						updates.push(node);
					} else if (currentNbreOf2Spots < nbreOf2spots) {
						currentNbreOf2Spots += 1;
						const node = {
							projectCode :   lightingProject2Spots.viewerProjectCode,
							SKU :           lightingProject2Spots.viewerComponentSKU,
							localId :       app.viewer.getLocalId(),
							parentLocalId : parentLocalId,
							matrix :        app.viewer.mtx(
								[ i, 0, 0 ],
								[ 1, 1, 1 ],
								0
							),
						};
						this.lightsNodesLocalId.push(node.localId);
						updates.push(node);
					}
				} else {
					const parentLocalId = this.viewerNode.idMapping.lightingWallRight;
					if (i < x + 0.6) {
						if (currentNbreOf3Spots < nbreOf3Spots) {
							currentNbreOf3Spots += 1;
							const node = {
								projectCode :   lightingProject3Spots.viewerProjectCode,
								SKU :           lightingProject3Spots.viewerComponentSKU,
								localId :       app.viewer.getLocalId(),
								parentLocalId : parentLocalId,
								matrix :        app.viewer.mtx(
									[ 0.6, 0, 0 ],
									[ 1, 1, 1 ],
									0
								),
							};
							this.lightsNodesLocalId.push(node.localId);
							updates.push(node);
						} else if (currentNbreOf2Spots < nbreOf2spots) {
							currentNbreOf2Spots += 1;
							const node = {
								projectCode :   lightingProject2Spots.viewerProjectCode,
								SKU :           lightingProject2Spots.viewerComponentSKU,
								localId :       app.viewer.getLocalId(),
								parentLocalId : parentLocalId,
								matrix :        app.viewer.mtx(
									[ 0.6, 0, 0 ],
									[ 1, 1, 1 ],
									0
								),
							};
							this.lightsNodesLocalId.push(node.localId);
							updates.push(node);
						}
						i = x + 0.6;
					} else {
						if (i > x + z - 1.1 && i < x + z - 0.5) {
							i = x + z - 1.2;
							if (currentNbreOf3Spots < nbreOf3Spots) {
								currentNbreOf3Spots += 1;
								const node = {
									projectCode :   lightingProject3Spots.viewerProjectCode,
									SKU :           lightingProject3Spots.viewerComponentSKU,
									localId :       app.viewer.getLocalId(),
									parentLocalId : parentLocalId,
									matrix :        app.viewer.mtx(
										[ i - x, 0, 0 ],
										[ 1, 1, 1 ],
										0
									),
								};
								this.lightsNodesLocalId.push(node.localId);
								updates.push(node);
							} else if (currentNbreOf2Spots < nbreOf2spots) {
								currentNbreOf2Spots += 1;
								const node = {
									projectCode :   lightingProject2Spots.viewerProjectCode,
									SKU :           lightingProject2Spots.viewerComponentSKU,
									localId :       app.viewer.getLocalId(),
									parentLocalId : parentLocalId,
									matrix :        app.viewer.mtx(
										[ i - x, 0, 0 ],
										[ 1, 1, 1 ],
										0
									),
								};
								this.lightsNodesLocalId.push(node.localId);
								updates.push(node);
							}
						} else if (i <= x + z - 1.1) {
							if (currentNbreOf3Spots < nbreOf3Spots) {
								currentNbreOf3Spots += 1;
								const node = {
									projectCode :   lightingProject3Spots.viewerProjectCode,
									SKU :           lightingProject3Spots.viewerComponentSKU,
									localId :       app.viewer.getLocalId(),
									parentLocalId : parentLocalId,
									matrix :        app.viewer.mtx(
										[ i - x, 0, 0 ],
										[ 1, 1, 1 ],
										0
									),
								};
								this.lightsNodesLocalId.push(node.localId);
								updates.push(node);
							} else if (currentNbreOf2Spots < nbreOf2spots) {
								currentNbreOf2Spots += 1;
								const node = {
									projectCode :   lightingProject2Spots.viewerProjectCode,
									SKU :           lightingProject2Spots.viewerComponentSKU,
									localId :       app.viewer.getLocalId(),
									parentLocalId : parentLocalId,
									matrix :        app.viewer.mtx(
										[ i - x, 0, 0 ],
										[ 1, 1, 1 ],
										0
									),
								};
								this.lightsNodesLocalId.push(node.localId);
								updates.push(node);
							}
						}
					}
				}
			}
		} else if (layoutCurrentOption.id === 'shape-left-back-right') {
			const perim = x + (z * 2);
			let incr = 0;
			let start = 0;
			if (Math.round(perim) % 3 === 0) {
				incr = 3;
				start = 1.5;
			} else if (Math.round(perim) % 3 === 1) {
				incr = 10 / 3;
				start = 5 / 3;
			} else {
				incr = 8 / 3;
				start = 4 / 3;
			}
			const ratio = (nbreOf3Spots + nbreOf2spots) * incr / (perim - start);
			incr = incr / ratio;
			for (let i = start; i < perim; i += incr) {
				if ((i > startStorage - 0.5) && (i < endStorage + 0.5)) {
					if ((surface > 24 && product.id === 'siae') ||
						product.id === 'confort' ||
						product.id === 'business' ||
						product.id === 'prestige')
					{
						currentNbreOf3Spots += 1;
						if (i + incr < endStorage + 0.5) {
							i = endStorage + 1 - incr;
						}
					} else {
						i = endStorage + 0.7;
						const parentLocalId = this.viewerNode.idMapping.lightingWallBack;
						if (currentNbreOf3Spots < nbreOf3Spots) {
							currentNbreOf3Spots += 1;
							const node = {
								projectCode :   lightingProject3Spots.viewerProjectCode,
								SKU :           lightingProject3Spots.viewerComponentSKU,
								localId :       app.viewer.getLocalId(),
								parentLocalId : parentLocalId,
								matrix :        app.viewer.mtx(
									[ i - z, 0, 0 ],
									[ 1, 1, 1 ],
									0
								),
							};
							this.lightsNodesLocalId.push(node.localId);
							updates.push(node);
						} else if (currentNbreOf2Spots < nbreOf2spots) {
							currentNbreOf2Spots += 1;
							const node = {
								projectCode :   lightingProject2Spots.viewerProjectCode,
								SKU :           lightingProject2Spots.viewerComponentSKU,
								localId :       app.viewer.getLocalId(),
								parentLocalId : parentLocalId,
								matrix :        app.viewer.mtx(
									[ i - z, 0, 0 ],
									[ 1, 1, 1 ],
									0
								),
							};
							this.lightsNodesLocalId.push(node.localId);
							updates.push(node);
						}
					}
				} else if (i < z - 0.5) {
					const parentLocalId = this.viewerNode.idMapping.lightingWallLeft;
					if (currentNbreOf3Spots < nbreOf3Spots) {
						currentNbreOf3Spots += 1;
						const node = {
							projectCode :   lightingProject3Spots.viewerProjectCode,
							SKU :           lightingProject3Spots.viewerComponentSKU,
							localId :       app.viewer.getLocalId(),
							parentLocalId : parentLocalId,
							matrix :        app.viewer.mtx(
								[ i, 0, 0 ],
								[ 1, 1, 1 ],
								0
							),
						};
						this.lightsNodesLocalId.push(node.localId);
						updates.push(node);
					} else if (currentNbreOf2Spots < nbreOf2spots) {
						currentNbreOf2Spots += 1;
						const node = {
							projectCode :   lightingProject2Spots.viewerProjectCode,
							SKU :           lightingProject2Spots.viewerComponentSKU,
							localId :       app.viewer.getLocalId(),
							parentLocalId : parentLocalId,
							matrix :        app.viewer.mtx(
								[ i, 0, 0 ],
								[ 1, 1, 1 ],
								0
							),
						};
						this.lightsNodesLocalId.push(node.localId);
						updates.push(node);
					}

				} else if (i < x + z - 0.5) {
					if (i < z + 0.6) {
						const parentLocalId = this.viewerNode.idMapping.lightingWallBack;
						if (currentNbreOf3Spots < nbreOf3Spots) {
							currentNbreOf3Spots += 1;
							const node = {
								projectCode :   lightingProject3Spots.viewerProjectCode,
								SKU :           lightingProject3Spots.viewerComponentSKU,
								localId :       app.viewer.getLocalId(),
								parentLocalId : parentLocalId,
								matrix :        app.viewer.mtx(
									[ 0.6, 0, 0 ],
									[ 1, 1, 1 ],
									0
								),
							};
							this.lightsNodesLocalId.push(node.localId);
							updates.push(node);
						} else if (currentNbreOf2Spots < nbreOf2spots) {
							currentNbreOf2Spots += 1;
							const node = {
								projectCode :   lightingProject2Spots.viewerProjectCode,
								SKU :           lightingProject2Spots.viewerComponentSKU,
								localId :       app.viewer.getLocalId(),
								parentLocalId : parentLocalId,
								matrix :        app.viewer.mtx(
									[ 0.6, 0, 0 ],
									[ 1, 1, 1 ],
									0
								),
							};
							this.lightsNodesLocalId.push(node.localId);
							updates.push(node);
						}
						i = z + 0.6;
					} else {
						const parentLocalId = this.viewerNode.idMapping.lightingWallBack;
						if (currentNbreOf3Spots < nbreOf3Spots) {
							currentNbreOf3Spots += 1;
							const node = {
								projectCode :   lightingProject3Spots.viewerProjectCode,
								SKU :           lightingProject3Spots.viewerComponentSKU,
								localId :       app.viewer.getLocalId(),
								parentLocalId : parentLocalId,
								matrix :        app.viewer.mtx(
									[ i - z, 0, 0 ],
									[ 1, 1, 1 ],
									0
								),
							};
							this.lightsNodesLocalId.push(node.localId);
							updates.push(node);
						} else if (currentNbreOf2Spots < nbreOf2spots) {
							currentNbreOf2Spots += 1;
							const node = {
								projectCode :   lightingProject2Spots.viewerProjectCode,
								SKU :           lightingProject2Spots.viewerComponentSKU,
								localId :       app.viewer.getLocalId(),
								parentLocalId : parentLocalId,
								matrix :        app.viewer.mtx(
									[ i - z, 0, 0 ],
									[ 1, 1, 1 ],
									0
								),
							};
							this.lightsNodesLocalId.push(node.localId);
							updates.push(node);
						}
					}
				} else {
					const parentLocalId = this.viewerNode.idMapping.lightingWallRight;
					if (i < x + z + 0.6) {
						if (currentNbreOf3Spots < nbreOf3Spots) {
							currentNbreOf3Spots += 1;
							const node = {
								projectCode :   lightingProject3Spots.viewerProjectCode,
								SKU :           lightingProject3Spots.viewerComponentSKU,
								localId :       app.viewer.getLocalId(),
								parentLocalId : parentLocalId,
								matrix :        app.viewer.mtx(
									[ 0.6, 0, 0 ],
									[ 1, 1, 1 ],
									0
								),
							};
							this.lightsNodesLocalId.push(node.localId);
							updates.push(node);
						} else if (currentNbreOf2Spots < nbreOf2spots) {
							currentNbreOf2Spots += 1;
							const node = {
								projectCode :   lightingProject2Spots.viewerProjectCode,
								SKU :           lightingProject2Spots.viewerComponentSKU,
								localId :       app.viewer.getLocalId(),
								parentLocalId : parentLocalId,
								matrix :        app.viewer.mtx(
									[ 0.6, 0, 0 ],
									[ 1, 1, 1 ],
									0
								),
							};
							this.lightsNodesLocalId.push(node.localId);
							updates.push(node);
						}
						i = x + z + 0.6;
					} else {
						if (i > x + z * 2 - 1.1 && i < x + z * 2 - 0.5) {
							i = x + z * 2 - 1.2;
							if (currentNbreOf3Spots < nbreOf3Spots) {
								currentNbreOf3Spots += 1;
								const node = {
									projectCode :   lightingProject3Spots.viewerProjectCode,
									SKU :           lightingProject3Spots.viewerComponentSKU,
									localId :       app.viewer.getLocalId(),
									parentLocalId : parentLocalId,
									matrix :        app.viewer.mtx(
										[ i - x - z, 0, 0 ],
										[ 1, 1, 1 ],
										0
									),
								};
								this.lightsNodesLocalId.push(node.localId);
								updates.push(node);
							} else if (currentNbreOf2Spots < nbreOf2spots) {
								currentNbreOf2Spots += 1;
								const node = {
									projectCode :   lightingProject2Spots.viewerProjectCode,
									SKU :           lightingProject2Spots.viewerComponentSKU,
									localId :       app.viewer.getLocalId(),
									parentLocalId : parentLocalId,
									matrix :        app.viewer.mtx(
										[ i - x - z, 0, 0 ],
										[ 1, 1, 1 ],
										0
									),
								};
								this.lightsNodesLocalId.push(node.localId);
								updates.push(node);
							}
						} else if (i <= x + z * 2 - 1.1) {
							if (currentNbreOf3Spots < nbreOf3Spots) {
								currentNbreOf3Spots += 1;
								const node = {
									projectCode :   lightingProject3Spots.viewerProjectCode,
									SKU :           lightingProject3Spots.viewerComponentSKU,
									localId :       app.viewer.getLocalId(),
									parentLocalId : parentLocalId,
									matrix :        app.viewer.mtx(
										[ i - x - z, 0, 0 ],
										[ 1, 1, 1 ],
										0
									),
								};
								this.lightsNodesLocalId.push(node.localId);
								updates.push(node);
							} else if (currentNbreOf2Spots < nbreOf2spots) {
								currentNbreOf2Spots += 1;
								const node = {
									projectCode :   lightingProject2Spots.viewerProjectCode,
									SKU :           lightingProject2Spots.viewerComponentSKU,
									localId :       app.viewer.getLocalId(),
									parentLocalId : parentLocalId,
									matrix :        app.viewer.mtx(
										[ i - x - z, 0, 0 ],
										[ 1, 1, 1 ],
										0
									),
								};
								this.lightsNodesLocalId.push(node.localId);
								updates.push(node);
							}
						}
					}
				}
			}
		} else {
			throw STD.errors.user('Unknown layout name');
		}
		await app.viewer.addProductNodesInstances(updates);
	};

	async remove() {
		if (!this.viewerNode) {
			return;
		}
		await this.viewerNode.delete();
		this.viewerNode = null;
		this.lightsNodesLocalId = [];
	};

	override async component_start(startEvent : STD.events.Event<any, CM.Component<any, any>>) {
		await super.component_start(startEvent);
		if (!app.viewer.planner) {
			this.component_throwUserError('');
		}
		;
		(<any> this).plannerCategory = app.viewer.createCategory({
			name :        'lighting',
			menuActions : {
				edit :      false,
				rotate :    false,
				delete :    false,
				duplicate : false
			},
			products :    [
				{
					SKU :   '@lighting',
					nodes : [
						app.viewer.createEmptyNodeDescriptor('global', 'root', 1),
						app.viewer.createEmptyNodeDescriptor('lightingWallLeft', 'global', 1),
						app.viewer.createEmptyNodeDescriptor('lightingWallBack', 'global', 1),
						app.viewer.createEmptyNodeDescriptor('lightingWallRight', 'global', 1)
					]
				}
			]
		});


	}

	constructor(
		descriptor : ConstructorParameters<typeof CM.Component>[0],
		configuration : IStandingLightningComponentConfiguration
	)
	{
		super(descriptor, configuration);
	};
};
export namespace NS {
	export const Component = StandingLightningComponent;
	export type Component = StandingLightningComponent;
	export type Configuration = IStandingLightningComponentConfiguration;
}
;
// import manifestXMLSource from './.xml';

CM.declare({
	id :   'standing-lighting',
	type : StandingLightningComponent,
	// manifest : manifestXMLSource
});
